// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from '@/auth/clerkShim';
import useTournamentStore from '@store/tournamentStore';
import useLeagueStore from '@store/leagueStore';
import useRegistrationStore from '@store/registrationStore';
import { useOutletContext, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import helpers from '@/utils/helpers';

interface CloneRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  registrationToClone: any;
}

export default function CloneRegistrationModal({ 
  isOpen, 
  onClose, 
  registrationToClone 
}: CloneRegistrationModalProps) {
  const { getToken } = useAuth();
  const { leagueName } = useOutletContext() as { leagueName?: string };
  const navigate = useNavigate();

  // Form state
  const [contestType, setContestType] = useState<'tournament' | 'season'>('tournament');
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | undefined>(undefined);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Store hooks
  const { tournaments, getTournamentsByLeagueName } = useTournamentStore((state) => ({
    tournaments: state.tournaments,
    getTournamentsByLeagueName: state.getTournamentsByLeagueName,
  }));

  const { leagues: leagueContests, getLeaguesByOrganizationName } = useLeagueStore((state: any) => ({
    leagues: state.leagues,
    getLeaguesByOrganizationName: state.getLeaguesByOrganizationName,
  }));

  const { addRegistration } = useRegistrationStore((state) => ({
    addRegistration: state.addRegistration(getToken),
  }));

  // Load tournaments and seasons when modal opens
  useEffect(() => {
    if (isOpen) {
      getTournamentsByLeagueName({ leagueName });
      getLeaguesByOrganizationName(leagueName);
      
      // Set the contest type from the cloned registration (read-only)
      if (registrationToClone) {
        const type = registrationToClone.contestType === 'tournament' ? 'tournament' : 'season';
        setContestType(type);
      }
    }
  }, [isOpen, leagueName, registrationToClone, getTournamentsByLeagueName, getLeaguesByOrganizationName]);


  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setContestType('tournament');
      setSelectedTournamentId(undefined);
      setSelectedLeagueId(undefined);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Get available tournaments and seasons (excluding those with registrations)
  const availableTournaments = _.filter(tournaments, (tournament) => !tournament.registration);
  const availableLeagues = _.orderBy(
    _.filter(leagueContests, (season) => season.contestType === 'season' && !season.registration),
    ['startDate', 'createdAt'],
    ['desc', 'desc']
  );

  const handleSubmit = async () => {
    if (!registrationToClone) {
      return;
    }

    if (contestType === 'tournament' && !selectedTournamentId) {
      return;
    }

    // For season type, validate selected season
    if (contestType === 'season' && !selectedLeagueId) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the cloned registration data (name will come from tournament/season)
      const clonedData = {
        ...registrationToClone,
        _id: undefined, // Remove the original ID
        contestType: contestType,
        ...(contestType === 'tournament'
          ? { tournament: selectedTournamentId }
          : { contest: selectedLeagueId, league: selectedLeagueId }
        ),
        active: false, // New registration starts as inactive
        createdAt: undefined, // Let server set new creation date
        updatedAt: undefined, // Let server set new update date
      };

      // Remove fields that shouldn't be copied
      delete clonedData._id;
      delete clonedData.name; // Name comes from tournament/season
      delete clonedData.createdAt;
      delete clonedData.updatedAt;

      const result = await new Promise<any>((resolve, reject) => {
        addRegistration(clonedData, (result: any) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error('Failed to create registration'));
          }
        });
      });

      // Close modal and reset form
      onClose();
      
      // Land on the sectioned edit page to review the clone before activating.
      const registrationId = helpers.getObjId(result?._id);
      navigate(`/admin/registration/${registrationId}/edit`);
      
    } catch (error) {
      console.error('Error cloning registration:', error);
      // You might want to show an error message to the user
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = 
    isSubmitting ||
    (contestType === 'tournament' && !selectedTournamentId) ||
    (contestType === 'season' && !selectedLeagueId);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Clone Registration</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Tournament Selection */}
          {contestType === 'tournament' && (
            <div className="space-y-2">
              <Label htmlFor="tournamentSelect">Select Tournament</Label>
              <Select
                value={selectedTournamentId}
                onValueChange={setSelectedTournamentId}
              >
                <SelectTrigger id="tournamentSelect" className="w-full">
                  <SelectValue placeholder="Select a tournament" />
                </SelectTrigger>
                <SelectContent>
                  {availableTournaments.map((tournament) => (
                    <SelectItem 
                      key={tournament._id} 
                      value={tournament._id}
                    >
                      {tournament.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableTournaments.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No available tournaments. All tournaments already have registrations.
                </p>
              )}
            </div>
          )}

          {/* Season Selection */}
          {contestType === 'season' && (
            <div className="space-y-2">
              <Label htmlFor="seasonSelect">Select League</Label>
              <Select
                value={selectedLeagueId}
                onValueChange={setSelectedLeagueId}
              >
                <SelectTrigger id="seasonSelect" className="w-full">
                  <SelectValue placeholder="Select a league" />
                </SelectTrigger>
                <SelectContent>
                  {availableLeagues.map((season) => (
                    <SelectItem 
                      key={season._id} 
                      value={season._id}
                    >
                      {season.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableLeagues.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No available seasons. All seasons already have registrations.
                </p>
              )}
            </div>
          )}

          {/* Original Registration Info */}
          {registrationToClone && (selectedTournamentId || selectedLeagueId) && (
            <div className="space-y-2 p-3 bg-muted/50 rounded-md">
              <Label className="text-sm font-medium">Cloning settings from:</Label>
              <div className="text-sm text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-1">
                <p><strong>Type:</strong> {_.capitalize(registrationToClone.contestType)}</p>
                <p><strong>Sport:</strong> {_.capitalize(registrationToClone.sport || registrationToClone.sports?.[0] || 'N/A')}</p>
                {registrationToClone.startDate && (
                  <p><strong>Start Date:</strong> {new Date(registrationToClone.startDate).toLocaleDateString()}</p>
                )}
                {registrationToClone.deadlineDate && (
                  <p><strong>Deadline:</strong> {new Date(registrationToClone.deadlineDate).toLocaleDateString()}</p>
                )}
                <p><strong>Payments:</strong> {registrationToClone.hasPayments ? `Yes${registrationToClone.cost?.amount ? ` ($${registrationToClone.cost.amount}${registrationToClone.cost.perPlayer ? '/player' : registrationToClone.cost.perTeam ? '/team' : ''})` : ''}` : 'No'}</p>
                <p><strong>Require Players:</strong> {registrationToClone.requirePlayers ? 'Yes' : 'No'}</p>
                <p><strong>Allow Coaches:</strong> {registrationToClone.allowCoaches ? 'Yes' : 'No'}</p>
                <p><strong>Release Statement:</strong> {registrationToClone.requireReleaseStatement ? 'Yes' : 'No'}</p>
                {registrationToClone.settings?.rosterLock && (
                  <p><strong>Roster Lock:</strong> {registrationToClone.settings.rosterLockDate ? new Date(registrationToClone.settings.rosterLockDate).toLocaleDateString() : 'Yes'}</p>
                )}
                {registrationToClone.teamFields?.length > 0 && (
                  <p><strong>Team Fields:</strong> {registrationToClone.teamFields.length}</p>
                )}
                {registrationToClone.managerFields?.length > 0 && (
                  <p><strong>Manager Fields:</strong> {registrationToClone.managerFields.length}</p>
                )}
                {registrationToClone.fields?.length > 0 && (
                  <p className="col-span-2"><strong>Player Fields:</strong> {registrationToClone.fields.map((f) => f.name).join(', ')}</p>
                )}
                {registrationToClone.playerWaivers?.length > 0 && (
                  <p><strong>Player Waivers:</strong> {registrationToClone.playerWaivers.length}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? 'Creating...' : 'Clone Registration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
